/**
 * API Response Types and Interfaces
 * Type definitions for all API responses and data structures
 */

/**
 * Dashboard Counts API Response Structure
 * @typedef {Object} DashboardCountsResponse
 * @property {boolean} success - Request success status
 * @property {DashboardData} data - Dashboard statistics data
 * @property {UserInfo} user - User information and permissions
 * @property {UsageInfo} usage - API usage statistics
 * @property {RateLimitInfo} rateLimit - Rate limit information
 * @property {string} timestamp - Response timestamp
 */

/**
 * Dashboard Statistics Data
 * @typedef {Object} DashboardData
 * @property {number} TrackedFilms - Number of films being tracked
 * @property {number} TrackedTalent - Number of talent being tracked
 * @property {number} CastCount - Total cast/talent count
 * @property {number} MovieCount - Total movie count for comps
 * @property {number} TotalFilmTrackingInterviews - Total interviews conducted
 * @property {number} TotalFilmTrackingUniqueParticipants - Unique participants in surveys
 * @property {number} TotalFilmTrackingSurveyWaves - Total survey waves completed
 * @property {string|null} LastSurveyDate - Date of last survey completion
 * @property {string|null} TrackingThroughDate - Latest date being tracked
 * @property {string} DataSource - Source of data (direct_queries, materialized, etc.)
 * @property {string} LastRefresh - Timestamp when data was last refreshed
 * @property {string} DataAsOf - Timestamp indicating data currency
 */

/**
 * User Information
 * @typedef {Object} UserInfo
 * @property {string} keyId - API key identifier
 * @property {string} userName - User display name
 * @property {string} userType - User type (internal, external, etc.)
 * @property {string[]} permissions - User permissions array
 * @property {string} rateLimitTier - Rate limit tier
 * @property {string} subscriptionTier - Subscription tier
 */

/**
 * Usage Information
 * @typedef {Object} UsageInfo
 * @property {number} totalRequests - Total requests made
 * @property {number} monthlyRequests - Requests this month
 * @property {number} dailyRequests - Requests today
 * @property {number|null} monthlyQuota - Monthly quota limit
 * @property {number|null} remainingQuota - Remaining quota
 */

/**
 * Rate Limit Information
 * @typedef {Object} RateLimitInfo
 * @property {number} limit - Rate limit per time window
 * @property {number} remaining - Remaining requests in window
 * @property {string} resetTime - When the rate limit resets
 */

/**
 * API Error Response Structure
 * @typedef {Object} ApiErrorResponse
 * @property {boolean} success - Always false for errors
 * @property {string} error - Error message
 * @property {string} [code] - Error code if available
 * @property {string} timestamp - Error timestamp
 */

/**
 * Generic API Response Wrapper
 * @template T
 * @typedef {Object} ApiResponse
 * @property {boolean} success - Request success status
 * @property {T} [data] - Response data (present on success)
 * @property {string} [error] - Error message (present on failure)
 * @property {string} timestamp - Response timestamp
 */

// Export types for runtime validation
export const ApiResponseSchema = {
  dashboardCounts: {
    required: ['success', 'data', 'timestamp'],
    dataRequired: ['TrackedFilms', 'TrackedTalent', 'CastCount', 'MovieCount']
  }
};

/**
 * Validate dashboard counts response
 * @param {any} response - Response to validate
 * @returns {boolean} - True if valid
 */
export const validateDashboardCountsResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const schema = ApiResponseSchema.dashboardCounts;
  
  // Check required top-level fields
  for (const field of schema.required) {
    if (!(field in response)) {
      console.error(`❌ Missing required field: ${field}`);
      return false;
    }
  }

  // Check success status
  if (response.success !== true) {
    console.error('❌ Response indicates failure');
    return false;
  }

  // Check required data fields
  if (!response.data || typeof response.data !== 'object') {
    console.error('❌ Missing or invalid data object');
    return false;
  }

  for (const field of schema.dataRequired) {
    if (!(field in response.data) || typeof response.data[field] !== 'number') {
      console.error(`❌ Missing or invalid data field: ${field}`);
      return false;
    }
  }

  return true;
};

/**
 * Transform API response to UI-expected format
 * @param {DashboardCountsResponse} apiResponse - API response
 * @returns {Object} - UI-formatted data
 */
export const transformDashboardData = (apiResponse) => {
  if (!validateDashboardCountsResponse(apiResponse)) {
    throw new Error('Invalid dashboard response format');
  }

  const { data } = apiResponse;
  
  return {
    totalMovies: data.TrackedFilms,
    totalCast: data.CastCount,
    upcomingReleases: data.TrackedTalent,
    compFilms: data.MovieCount
  };
};

const apiTypesDefault = {
  ApiResponseSchema,
  validateDashboardCountsResponse,
  transformDashboardData
};

export default apiTypesDefault;
